import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

export const statusFlowOptions = [
  'Realizado',
  'Em Análise',
  'Pendências',
  'Indeferido',
  'Deferido',
  'Deferido Transferido',
  'Deferido Devolvido',
  'Aceite Confirmado'
];
export const statusTransferenciaOptions = [
  { label: 'Encontrado', value: 'encontrado' },
  { label: 'Capturado', value: 'capturado' },
  { label: 'Devolvido', value: 'devolvido' },
  { label: 'Problema', value: 'problema' }
];
export const situacaoOptions = ['Aberto', 'Pago', 'Baixado'];

export const money = value =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const date = value => (value ? moment(value).format('DD/MM/YYYY') : '—');

export const statusClass = value => (value || '').toLowerCase().replaceAll(' ', '-');

export const statusChip = (value, props = {}) => (
  <>
    {value ? (
      <Chip className={`flow-model-chip ${statusClass(value)}`} label={value} size="small" {...props} />
    ) : (
      '—'
    )}
  </>
);

const SelectField = ({ label, value, onChange, options, emptyLabel }) => (
  <Box className="flow-filter-field">
    <Typography className="flow-filter-label">{label}</Typography>
    <FormControl fullWidth size="small">
      <Select
        displayEmpty
        value={value}
        onChange={event => onChange(event.target.value)}
        renderValue={selected => {
          if (!selected) return emptyLabel;
          const match = options.find(option =>
            typeof option === 'string' ? option === selected : option.value === selected
          );
          const matchLabel = typeof match === 'string' ? match : match?.label;
          return matchLabel || selected;
        }}
      >
        <MenuItem value="">{emptyLabel}</MenuItem>
        {options.map(option => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          return (
            <MenuItem key={optionValue} value={optionValue}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  </Box>
);

const DateField = ({ label, value, onChange, placeholder = 'Selecionar data' }) => (
  <Box className="flow-filter-field">
    <Typography className="flow-filter-label">{label}</Typography>
    <DatePicker
      value={value ? moment(value) : null}
      onChange={nextValue => onChange(nextValue?.format('YYYY-MM-DD') || '')}
      slotProps={{ textField: { fullWidth: true, size: 'small', placeholder } }}
    />
  </Box>
);

export const FlowFilterFields = ({ filters, setFilters, parceiros, carregandoParceiros }) => {
  const set = (key, value) => setFilters(previous => ({ ...previous, [key]: value }));

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
      <Box className="flow-filter-groups">
        <Box className="flow-filter-group flow-filter-group-primary">
          <Box className="flow-filter-group-heading">
            <Box>
              <Typography className="flow-filter-group-title">Visão One7</Typography>
              <Typography className="flow-filter-group-caption">
                Identifique o título na operação
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={8}>
              <Box className="flow-filter-field">
                <Typography className="flow-filter-label">Parceiros</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  limitTags={1}
                  disableCloseOnSelect
                  options={parceiros}
                  value={parceiros.filter(parceiro => filters.parceiros.includes(parceiro.value))}
                  loading={carregandoParceiros}
                  disabled={carregandoParceiros}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={option => option.label}
                  onChange={(_, values) =>
                    set(
                      'parceiros',
                      values.map(parceiro => parceiro.value)
                    )
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        carregandoParceiros ? 'Carregando parceiros...' : 'Todos os parceiros'
                      }
                      size="small"
                    />
                  )}
                  className="flow-filter-select-multiple"
                />
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Box className="flow-filter-field">
                <Typography className="flow-filter-label">Tipo Operação</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    name="tipo"
                    id="tipo"
                    displayEmpty
                    value={filters.type}
                    onChange={event => set('type', event.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="avencer">A Vencer</MenuItem>
                    <MenuItem value="vencidos">Vencidos</MenuItem>
                    <MenuItem value="baixados">Baixados</MenuItem>
                    <MenuItem value="amortizados">Amortizados</MenuItem>
                    <MenuItem value="em_aberto">Em Aberto</MenuItem>
                    <MenuItem value="recomprados">Recomprados</MenuItem>
                    <MenuItem value="perdidos">Perdidos</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box className="flow-filter-field">
                <Typography className="flow-filter-label">Nº Borderô</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ex.: CF-24001"
                  value={filters.bordero}
                  onChange={event => set('bordero', event.target.value)}
                />
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box className="flow-filter-field">
                <Typography className="flow-filter-label">Nº Documento</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ex.: DOC-1001"
                  value={filters.ndoc}
                  onChange={event => set('ndoc', event.target.value)}
                />
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="Data Aceite"
                value={filters.dataDe}
                onChange={value => set('dataDe', value)}
                placeholder="Data inicial"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="&nbsp;"
                value={filters.dataAte}
                onChange={value => set('dataAte', value)}
                placeholder="Data final"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="Data Liquidação"
                value={filters.liquidacaoDe}
                onChange={value => set('liquidacaoDe', value)}
                placeholder="Data inicial"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="&nbsp;"
                value={filters.liquidacaoAte}
                onChange={value => set('liquidacaoAte', value)}
                placeholder="Data final"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                label="Status transferência"
                value={filters.statusTransfer}
                onChange={value => set('statusTransfer', value)}
                options={statusTransferenciaOptions}
                emptyLabel="Todos"
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="flow-filter-group flow-filter-group-partner">
          <Box className="flow-filter-group-heading">
            <Box>
              <Typography className="flow-filter-group-title">Visão do parceiro</Typography>
              <Typography className="flow-filter-group-caption">
                Acompanhe o que está em trânsito
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <SelectField
                label="Status Flow"
                value={filters.statusFlow}
                onChange={value => set('statusFlow', value)}
                options={statusFlowOptions}
                emptyLabel="Todos os status"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <DateField
                label="Vencimento"
                value={filters.vencimentoParceiroDe}
                onChange={value => set('vencimentoParceiroDe', value)}
                placeholder="Data inicial"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <DateField
                label="&nbsp;"
                value={filters.vencimentoParceiroAte}
                onChange={value => set('vencimentoParceiroAte', value)}
                placeholder="Data final"
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="flow-filter-group flow-filter-group-bank">
          <Box className="flow-filter-group-heading">
            <Box>
              <Typography className="flow-filter-group-title">Bancarizador</Typography>
              <Typography className="flow-filter-group-caption">
                Feche o ciclo financeiro da conciliação
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <SelectField
                label="Situação"
                value={filters.situacao}
                onChange={value => set('situacao', value)}
                options={situacaoOptions}
                emptyLabel="Todas as situações"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="Vencimento"
                value={filters.vencimentoBancarizadorDe}
                onChange={value => set('vencimentoBancarizadorDe', value)}
                placeholder="Data inicial"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DateField
                label="&nbsp;"
                value={filters.vencimentoBancarizadorAte}
                onChange={value => set('vencimentoBancarizadorAte', value)}
                placeholder="Data final"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export const FilterHeader = ({ children, onClose, title = 'Filtros avançados' }) => (
  <Box className="flow-filter-header">
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box className="flow-filter-header-icon">
        <FilterAltOutlinedIcon fontSize="small" />
      </Box>
      <Box>
        <Typography className="flow-filter-header-title">{title}</Typography>
        <Typography className="flow-filter-header-caption">
          Combine os critérios para encontrar os títulos
        </Typography>
      </Box>
    </Stack>
    <Tooltip title="Fechar filtros">
      <IconButton onClick={onClose} size="small">
        <CloseRoundedIcon />
      </IconButton>
    </Tooltip>
    {children}
  </Box>
);

export const DetailPanel = ({ item, onClose, onAction, compact = false, actionLoading = false }) => {
  if (!item) {
    return (
      <Box className={`flow-detail-empty ${compact ? 'compact' : ''}`}>
        <Box className="flow-detail-empty-icon">
          <FilterAltOutlinedIcon />
        </Box>
        <Typography className="flow-detail-empty-title">Selecione um título</Typography>
        <Typography className="flow-detail-empty-caption">
          Veja os detalhes da operação e execute uma ação com segurança.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={`flow-detail-panel ${compact ? 'compact' : ''}`}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box>
          <Typography className="flow-detail-kicker">Título selecionado</Typography>
          <Typography className="flow-detail-title">{item.ndoc}</Typography>
          <Typography className="flow-detail-subtitle">{item.parceiro}</Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" aria-label="Fechar detalhes">
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Stack>
      <Box className="flow-detail-status-row">
        {statusChip(item.status)}
        <Chip label={item.operationOriginal} size="small" className="flow-operation-chip" />
        <Chip label={item.carteira} size="small" className="flow-type-chip" />
      </Box>
      <Box className="flow-detail-value">
        <Typography>Valor do título</Typography>
        <strong>{money(item.valor)}</strong>
      </Box>
      <Box className="flow-detail-grid">
        {[
          ['Borderô', item.bordero],
          ['Vencimento', date(item.vencimento)],
          ['Liquidação', date(item.liquidacaoData)],
          ['Tipo de liquidação', item.liquidacaoTipo || 'Aguardando'],
          ['Bancarizador', item.statusTransferenciaBancarizador],
          ['Situação', item.situacao]
        ].map(([label, value]) => (
          <Box key={label}>
            <Typography className="flow-detail-label">{label}</Typography>
            <Typography className="flow-detail-data">{value || '—'}</Typography>
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1} className="flow-detail-actions">
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineRoundedIcon />}
          disabled={item.status && ['Capturado', 'Conciliado'].includes(item.status)}
          onClick={() => onAction('capturar', item)}
          className="flow-detail-action-capture"
        >
          Capturar
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={
            actionLoading ? <CircularProgress size={18} color="inherit" /> : <ReplayOutlinedIcon />
          }
          disabled={actionLoading || (item.status && item.status !== 'Capturado')}
          onClick={() => onAction('devolver', item)}
          className="flow-detail-action-return"
        >
          {actionLoading ? 'Devolvendo...' : 'Devolver'}
        </Button>
      </Stack>
    </Box>
  );
};

export const DetailDrawer = ({ item, onClose, onAction, actionLoading = false }) => (
  <Drawer anchor="right" open={Boolean(item)} onClose={onClose} className="flow-detail-drawer">
    <DetailPanel item={item} onClose={onClose} onAction={onAction} actionLoading={actionLoading} />
  </Drawer>
);

export const ActionDialogs = ({
  actionRow,
  setActionRow,
  action,
  confirm,
  setConfirm,
  motivo,
  setMotivo,
  confirmAction,
  actionLoading = false
}) => (
  <>
    <Dialog
      open={Boolean(actionRow)}
      onClose={() => setActionRow(null)}
      fullWidth
      maxWidth="xs"
      className="flow-action-dialog"
    >
      <DialogTitle>Ações do título</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">
          {actionRow?.ndoc} · {actionRow?.parceiro}
        </Typography>
        <Stack spacing={1.25} className="flow-dialog-buttons">
          <Button
            variant="contained"
            startIcon={<CheckCircleOutlineRoundedIcon />}
            disabled={['Capturado', 'Conciliado'].includes(actionRow?.status)}
            onClick={() => {
              setActionRow(null);
              action('capturar', actionRow);
            }}
          >
            Capturar título
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ReplayOutlinedIcon />}
            disabled={actionRow?.status === 'Devolvido'}
            onClick={() => {
              setActionRow(null);
              action('devolver', actionRow);
            }}
          >
            Devolver título
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
    <Dialog
      open={Boolean(confirm)}
      onClose={() => setConfirm(null)}
      fullWidth
      maxWidth="sm"
      className="flow-confirm-dialog"
    >
      <DialogTitle>
        {
          {
            capturar: 'Capturar título?',
            capturar_lote: 'Capturar títulos selecionados?',
            devolver: 'Devolver título?'
          }[confirm?.type]
        }
      </DialogTitle>
      <DialogContent>
        {confirm?.type === 'capturar' && (
          <Typography>
            O título <strong>{confirm?.item?.ndoc}</strong> será capturado para conciliação.
          </Typography>
        )}
        {confirm?.type === 'capturar_lote' && (
          <Typography>
            <strong>{confirm?.itens?.length}</strong> título(s) selecionado(s) serão capturados para
            conciliação.
          </Typography>
        )}
        {confirm?.type === 'devolver' && (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo da devolução"
            value={motivo}
            onChange={event => setMotivo(event.target.value)}
            className="flow-motivo"
            required
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className="btn-cancelar-captura"
          onClick={() => setConfirm(null)}
          disabled={actionLoading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          className="btn-confirmar-captura"
          color={confirm?.type === 'devolver' ? 'error' : 'primary'}
          onClick={confirmAction}
          disabled={actionLoading}
          endIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {!actionLoading ? 'Confirmar' : confirm?.type === 'devolver' ? 'Devolvendo' : 'Capturando'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
);
